/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { getPayloadObject, getTelemetryAttributes, ITelemetryContent, onTelemetryClick } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { ILinksData } from '../interactive-feature-item.props.autogenerated';

/**
 * Interactive Feature links.
 */
export interface IInteractiveFeatureLinks {
    links: ILinksData[];
    requestContext: Msdyn365.IRequestContext;
    telemetryContent: ITelemetryContent;
    onTextChange?(index: number): (event: Msdyn365.ContentEditableEvent) => void;
}

/**
 *
 * IInteractiveFeatureLinks component.
 * @extends {React.PureComponent<IInteractiveFeatureLinks>}
 */
export class InteractiveFeatureCollectionLinks extends React.PureComponent<IInteractiveFeatureLinks> {
    public render(): JSX.Element {
        const editableLinks = this._mapEditableLinks(this.props.links);
        return (
            <div className='ms-interactive-feature-collection__links'>
                {editableLinks && ArrayExtensions.hasElements(editableLinks) ? (
                    <Msdyn365.Links
                        links={editableLinks}
                        editProps={{ onTextChange: this.props.onTextChange, requestContext: this.props.requestContext }}
                    />
                ) : null}
            </div>
        );
    }

    /**
     * IInteractiveFeatureLinks component.
     * @param linkdata - Link data.
     * @returns Editable links.
     */
    private readonly _mapEditableLinks = (linkdata?: ILinksData[]): Msdyn365.ILinksData[] | null => {
        if (!linkdata || !ArrayExtensions.hasElements(linkdata)) {
            return null;
        }
        const editableLinks: Msdyn365.ILinksData[] = [];
        for (const link of linkdata) {
            // Construct telemetry attribute to render
            const payLoad = getPayloadObject('click', this.props.telemetryContent, '', '');
            const linkText = link.linkText ? link.linkText : '';
            payLoad.contentAction.etext = linkText;
            const attributes = getTelemetryAttributes(this.props.telemetryContent, payLoad);
            const editableLink: Msdyn365.ILinksData = {
                ariaLabel: link.ariaLabel,
                className: 'ms-interactive-feature-collection__link',
                linkText: link.linkText,
                linkUrl: link.linkUrl.destinationUrl,
                openInNewTab: link.openInNewTab,
                role: 'button',
                additionalProperties: attributes,
                onClick: onTelemetryClick(this.props.telemetryContent, payLoad, linkText)
            };
            editableLinks.push(editableLink);
        }

        return editableLinks;
    };
}
export default InteractiveFeatureCollectionLinks;
